/** Display types from ai_v5 §2.5 */
export const PULLOUT_DISPLAY = Object.freeze({
  HINTS: "hints",
  BANNER: "banner",
  MODAL: "modal",
});

/** Higher priority first — only one pullout is shown at a time. */
const PULLOUT_PRIORITY = [
  PULLOUT_DISPLAY.MODAL,
  PULLOUT_DISPLAY.BANNER,
  PULLOUT_DISPLAY.HINTS,
];

const PERIOD_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

const parsePeriodDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const match = PERIOD_RE.exec(raw);
  if (match) {
    const [, y, mo, d, h, mi, s] = match;
    const date = new Date(+y, +mo - 1, +d, +h, +mi, +s);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/** Web reader: only show when device includes Browser. */
export const isDeviceAllowed = (device) =>
  Array.isArray(device) && device.some((entry) => String(entry) === "Browser");

export const isInPeriod = (period, now = new Date()) => {
  if (!period || typeof period !== "object") return false;
  const start = parsePeriodDate(period.start);
  const end = parsePeriodDate(period.end);
  if (!start || !end) return false;
  const ts = now.getTime();
  return ts >= start.getTime() && ts <= end.getTime();
};

/**
 * True when reference_page is in the currently visible page range.
 * Single page: start === end. Double-page / spread: start..end inclusive.
 */
export const isForPage = (pullout, currentPage, currentPageEnd) => {
  const ref = Number(pullout?.reference_page);
  if (!Number.isFinite(ref) || ref <= 0) return false;

  const start = Number(currentPage);
  if (!Number.isFinite(start) || start <= 0) return false;

  const endRaw = Number(currentPageEnd);
  const end = Number.isFinite(endRaw) && endRaw >= start ? endRaw : start;

  return ref >= start && ref <= end;
};

/** Short stable fingerprint so same title/page/url pullouts stay distinct. */
const contentFingerprint = (pullout) => {
  const raw = String(pullout?.html || pullout?.text || "");
  if (!raw) return "0";
  // Sample head/mid/tail + length — enough to tell variants apart without hashing MBs.
  const mid = raw.length >> 1;
  const sample =
    raw.length <= 384
      ? raw
      : `${raw.length}|${raw.slice(0, 128)}|${raw.slice(mid, mid + 128)}|${raw.slice(-128)}`;
  let hash = 0;
  for (let i = 0; i < sample.length; i += 1) {
    hash = (hash << 5) - hash + sample.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
};

/** Stable id for dismiss tracking within a document session. */
export const getPulloutId = (pullout) => {
  if (!pullout || typeof pullout !== "object") return "";
  return [
    pullout.display || "",
    pullout.title || "",
    pullout.reference_page ?? "",
    pullout.url || "",
    contentFingerprint(pullout),
  ].join("::");
};

/**
 * Pick the single pullout to show right now.
 * Filters: Browser device, period window, reference_page (visible range), dismissals.
 * Priority: modal → banner → hints.
 * Within a display type, API order is preserved — dismissing one surfaces the next
 * matching pullout of that type (then lower-priority types).
 */
export const selectActivePullout = (
  pullouts,
  { currentPage, currentPageEnd, dismissedIds, now = new Date() } = {},
) => {
  if (!Array.isArray(pullouts) || !pullouts.length) return null;

  const dismissed =
    dismissedIds instanceof Set
      ? dismissedIds
      : new Set(Array.isArray(dismissedIds) ? dismissedIds : []);

  const byDisplay = Object.create(null);

  for (const item of pullouts) {
    if (!item || typeof item !== "object") continue;
    const display = String(item.display || "").toLowerCase();
    if (!PULLOUT_PRIORITY.includes(display)) continue;

    const id = getPulloutId(item);
    if (!id || dismissed.has(id)) continue;
    if (!isDeviceAllowed(item.device)) continue;
    if (!isInPeriod(item.period, now)) continue;
    if (!isForPage(item, currentPage, currentPageEnd)) continue;

    // First non-dismissed match per display type (API order → queue on dismiss).
    if (!byDisplay[display]) byDisplay[display] = item;
  }

  for (const display of PULLOUT_PRIORITY) {
    if (byDisplay[display]) return byDisplay[display];
  }
  return null;
};
